import { useEffect, useState } from "react";
import { sb } from "@/lib/sb";
import { SafeImg } from "@/components/SafeImg";
import { PlaceholderImage } from "@/components/layout/PlaceholderImage";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { buildSrcSet, pickSrc, type VariantMap } from "@/lib/image-srcset";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

export type GalleryProject = {
  id: string;
  name: string;
  category: string;
  description: string | null;
  location: string | null;
  date_completed: string | null;
  photos: string[] | null;
  primary_photo_index: number | null;
  before_photo: string | null;
  after_photo: string | null;
  before_label: string | null;
  after_label: string | null;
  before_after_enabled: boolean | null;
  photo_variants: Record<string, VariantMap> | null;
};

const SELECT_COLUMNS =
  "id, name, category, description, location, date_completed, photos, primary_photo_index, before_photo, after_photo, before_label, after_label, before_after_enabled, photo_variants";

export function projectSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function useCategoryProjects(categoryTitle: string) {
  const [projects, setProjects] = useState<GalleryProject[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data, error } = await sb
        .from("projects")
        .select(SELECT_COLUMNS)
        .eq("is_published", true)
        .eq("category", categoryTitle)
        .order("sort_order", { ascending: true })
        .order("date_completed", { ascending: false, nullsFirst: false });
      if (cancelled || error) return;
      setProjects((data as GalleryProject[]) ?? []);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [categoryTitle]);

  return projects;
}

function primaryPhotoIndex(p: GalleryProject) {
  const photos = p.photos ?? [];
  if (photos.length === 0) return -1;
  return Math.min(Math.max(p.primary_photo_index ?? 0, 0), photos.length - 1);
}

function GalleryThumb({ project }: { project: GalleryProject }) {
  const photos = project.photos ?? [];
  const start = primaryPhotoIndex(project);
  const order =
    start >= 0 ? [start, ...photos.map((_, i) => i).filter((i) => i !== start)] : [];
  const [attempt, setAttempt] = useState(0);

  if (order.length === 0 || attempt >= order.length) {
    return <PlaceholderImage label={project.name} />;
  }

  const photo = photos[order[attempt]];
  const variants = project.photo_variants?.[photo] ?? null;
  const srcSet = buildSrcSet(variants);
  const src = pickSrc(variants, photo);

  return (
    <SafeImg
      key={photo}
      src={src}
      {...(srcSet ? { srcSet } : {})}
      sizes="(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw"
      alt={`${project.name} completed project photo`}
      loading="lazy"
      decoding="async"
      width={800}
      height={600}
      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      onFail={() => setAttempt((a) => a + 1)}
    />
  );
}

function ModalPhoto({ project, photo }: { project: GalleryProject; photo: string }) {
  const variants = project.photo_variants?.[photo] ?? null;
  const srcSet = buildSrcSet(variants);
  const src = pickSrc(variants, photo);
  return (
    <SafeImg
      src={src}
      {...(srcSet ? { srcSet } : {})}
      sizes="(min-width:768px) 60vw, 100vw"
      alt={`${project.name} project photo`}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover"
    />
  );
}

function ProjectBody({ project }: { project: GalleryProject }) {
  const photos = project.photos ?? [];
  const showBA =
    !!project.before_after_enabled && !!project.before_photo && !!project.after_photo;
  const date = project.date_completed
    ? new Date(`${project.date_completed}T00:00:00`).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="space-y-5">
      {(showBA || photos.length > 0) && (
        <Carousel
          opts={{
            loop: true,
            watchDrag: (_api, evt) =>
              !(evt.target as Element | null)?.closest("[data-noswipe]"),
          }}
          className="w-full"
        >
          <CarouselContent>
            {showBA && (
              <CarouselItem key="ba">
                <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded">
                  <BeforeAfterSlider
                    before={project.before_photo!}
                    after={project.after_photo!}
                    alt={project.name}
                    beforeLabel={project.before_label || undefined}
                    afterLabel={project.after_label || undefined}
                  />
                </div>
              </CarouselItem>
            )}
            {photos.map((photo) => (
              <CarouselItem key={photo}>
                <div className="relative aspect-[4/3] overflow-hidden bg-muted rounded">
                  <ModalPhoto project={project} photo={photo} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-2 mt-4">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>
      )}

      <div className="text-sm text-ink/70 space-y-1">
        <p className="font-display font-semibold uppercase tracking-wide text-brand text-xs">
          {project.category}
        </p>
        {project.location && <p>{project.location}</p>}
        {date && <p>Completed {date}</p>}
      </div>

      {project.description && (
        <p className="text-ink/80 leading-relaxed">{project.description}</p>
      )}
    </div>
  );
}

export function ProjectDetailModal({
  project,
  onClose,
}: {
  project: GalleryProject | null;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const open = !!project;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
        <DrawerContent className="max-h-[95vh]">
          {project && (
            <>
              <DrawerHeader className="text-left">
                <DrawerTitle className="font-display font-bold uppercase text-xl text-ink">
                  {project.name}
                </DrawerTitle>
                <DrawerDescription className="sr-only">
                  Project details for {project.name}
                </DrawerDescription>
              </DrawerHeader>
              <div className="px-4 pb-8 overflow-y-auto">
                <ProjectBody project={project} />
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {project && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-bold uppercase text-2xl text-ink">
                {project.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Project details for {project.name}
              </DialogDescription>
            </DialogHeader>
            <ProjectBody project={project} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ServiceProjectGallery({
  projects,
  onSelect,
}: {
  projects: GalleryProject[];
  onSelect: (p: GalleryProject) => void;
}) {
  if (projects.length === 0) return null;

  return (
    <div className="mt-16">
      <h4 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink text-center">
        Completed Projects
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {projects.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p)}
            className="group text-left bg-white rounded overflow-hidden shadow-sm min-h-[44px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              <GalleryThumb project={p} />
            </div>
            <div className="p-4">
              <span className="font-display font-bold uppercase text-base text-ink">
                {p.name}
              </span>
              {p.location && (
                <span className="block text-sm text-ink/60 mt-1">{p.location}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
